// src/components/tutorials/JS_Array_Iterations.js
import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };
  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

// 🔹 Random border color generator
function getRandomColor() {
  const colors = [
    "#2563eb",
    "#16a34a",
    "#dc2626",
    "#d97706",
    "#9333ea",
    "#0891b2",
    "#f59e0b",
    "#e11d48",
    "#0ea5e9",
    "#84cc16",
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

// 🔹 Output Box Component
function OutputBox({ children }) {
  const borderColor = getRandomColor();
  return (
    <div
      className="output-box"
      style={{
        border: `2px dashed ${borderColor}`,
        transition: "border-color 0.3s ease",
        padding: "0.5rem 1rem",
        borderRadius: "10px",
        background: "#f9fafb",
        color: "#000",
        marginTop: "0.5rem",
      }}
    >
      {children}
    </div>
  );
}

export default function JS_Array_Iterations() {
  const refs = {
    forEach: useRef(null),
    map: useRef(null),
    flatMap: useRef(null),
    filter: useRef(null),
    reduce: useRef(null),
    reduceRight: useRef(null),
    every: useRef(null),
    some: useRef(null),
    from: useRef(null),
    keys: useRef(null),
    entries: useRef(null),
    withMethod: useRef(null),
    spread: useRef(null),
    rest: useRef(null),
  };

  const scrollTo = (key) => {
    const r = refs[key];
    if (r && r.current) {
      r.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const keyPoints = [
    { key: "forEach", label: "Array forEach()" },
    { key: "map", label: "Array map()" },
    { key: "flatMap", label: "Array flatMap()" },
    { key: "filter", label: "Array filter()" },
    { key: "reduce", label: "Array reduce()" },
    { key: "reduceRight", label: "Array reduceRight()" },
    { key: "every", label: "Array every()" },
    { key: "some", label: "Array some()" },
    { key: "from", label: "Array from()" },
    { key: "keys", label: "Array keys()" },
    { key: "entries", label: "Array entries()" },
    { key: "withMethod", label: "Array with()" },
    { key: "spread", label: "Array Spread (...)" },
    { key: "rest", label: "Array Rest (...)" },
  ];

  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">JavaScript Array Iteration Methods</h1>

      <p className="tutorial-intro">
        Array iteration methods allow you to loop, transform, and process array
        elements in powerful and expressive ways. Let’s explore each one.
      </p>

      {/* Key Points Navigation */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #fce7f3, #fbcfe8)",
        }}
      >
        <h1 style={{ color: "#9d174d", fontSize: "2.3rem", margin: 0 }}>
          Array Iteration Essentials
        </h1>
        <p style={{ fontSize: "1.05rem", marginTop: "0.5rem" }}>
          Click a topic below to view examples and explanations.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            marginTop: "1.25rem",
          }}
        >
          {keyPoints.map((l) => (
            <p key={l.key} style={{ margin: "0.25rem 0" }}>
              <button
                onClick={() => scrollTo(l.key)}
                style={{
                  background: "transparent",
                  border: "none",
                  textDecoration: "underline",
                  color: "#000",
                  cursor: "pointer",
                  fontSize: "1.05rem",
                }}
              >
                {l.label}
              </button>
            </p>
          ))}
        </div>
      </div>

      {/* 1️⃣ forEach() */}
      <div ref={refs.forEach} className="tutorial-section">
        <h2 className="section-title">1️⃣ Array forEach()</h2>
        <p>
          The{" "}
          <code
            className="highlight_code"
            style={{ fontSize: "1.3rem", fontWeight: "bold" }}
          >
            forEach()
          </code>{" "}
          method executes a provided function once for each array element.
        </p>
        <div className="code-demo-box">
          {" "}
          <CodeCopyBlock
            code={`const fruits = ["apple", "banana", "cherry"];
fruits.forEach(fruit => console.log(fruit));`}
          />
          <TryItButton
            code={`const fruits = ["apple", "banana", "cherry"];
fruits.forEach(fruit => console.log(fruit));`}
          />
        </div>
        <OutputBox>
          <pre>
            apple{"\n"}banana{"\n"}cherry
          </pre>
        </OutputBox>
      </div>

      {/* 2️⃣ map() */}
      <div ref={refs.map} className="tutorial-section">
        <h2 className="section-title">2️⃣ Array map()</h2>
        <p>
          The{" "}
          <code
            className="highlight_code"
            style={{ fontSize: "1.3rem", fontWeight: "bold" }}
          >
            map()
          </code>{" "}
          method creates a new array by applying a function to each element.
        </p>
        <div className="code-demo-box">
          {" "}
          <CodeCopyBlock
            code={`const numbers = [1, 2, 3];
const doubled = numbers.map(num => num * 2);
console.log(doubled);`}
          />
          <TryItButton
            code={`const numbers = [1, 2, 3];
const doubled = numbers.map(num => num * 2);
console.log(doubled);`}
          />
        </div>
        <OutputBox>
          <pre>[2, 4, 6]</pre>
        </OutputBox>
      </div>

      {/* 3️⃣ flatMap() */}
      <div ref={refs.flatMap} className="tutorial-section">
        <h2 className="section-title">3️⃣ Array flatMap()</h2>
        <p>
          The{" "}
          <code
            className="highlight_code"
            style={{ fontSize: "1.3rem", fontWeight: "bold" }}
          >
            flatMap()
          </code>{" "}
          method maps each element and then flattens the result into a new
          array.
        </p>
        <div className="code-demo-box">
          {" "}
          <CodeCopyBlock
            code={`const arr = [1, 2, 3];
const result = arr.flatMap(x => [x, x * 2]);
console.log(result);`}
          />
          <TryItButton
            code={`const arr = [1, 2, 3];
const result = arr.flatMap(x => [x, x * 2]);
console.log(result);`}
          />
        </div>
        <OutputBox>
          <pre>[1, 2, 2, 4, 3, 6]</pre>
        </OutputBox>
      </div>

      {/* 4️⃣ filter() */}
      <div ref={refs.filter} className="tutorial-section">
        <h2 className="section-title">4️⃣ Array filter()</h2>
        <p>
          The{" "}
          <code
            className="highlight_code"
            style={{ fontSize: "1.3rem", fontWeight: "bold" }}
          >
            filter()
          </code>{" "}
          method creates a new array with elements that pass a condition.
        </p>
        <div className="code-demo-box">
          {" "}
          <CodeCopyBlock
            code={`const numbers = [10, 25, 40];
const over20 = numbers.filter(n => n > 20);
console.log(over20);`}
          />
          <TryItButton
            code={`const numbers = [10, 25, 40];
const over20 = numbers.filter(n => n > 20);
console.log(over20);`}
          />
        </div>
        <OutputBox>
          <pre>[25, 40]</pre>
        </OutputBox>
      </div>

      {/* 5️⃣ reduce() */}
      <div ref={refs.reduce} className="tutorial-section">
        <h2 className="section-title">5️⃣ Array reduce()</h2>
        <p>
          The{" "}
          <code
            className="highlight_code"
            style={{ fontSize: "1.3rem", fontWeight: "bold" }}
          >
            reduce()
          </code>{" "}
          method reduces array values to a single value.
        </p>
        <div className="code-demo-box">
          {" "}
          <CodeCopyBlock
            code={`const numbers = [1, 2, 3, 4];
const sum = numbers.reduce((total, num) => total + num, 0);
console.log(sum);`}
          />
          <TryItButton
            code={`const numbers = [1, 2, 3, 4];
const sum = numbers.reduce((total, num) => total + num, 0);
console.log(sum);`}
          />
        </div>
        <OutputBox>
          <pre>10</pre>
        </OutputBox>
      </div>

      {/* 6️⃣ reduceRight() */}
      <div ref={refs.reduceRight} className="tutorial-section">
        <h2 className="section-title">6️⃣ Array reduceRight()</h2>
        <p>
          Works like{" "}
          <code
            className="highlight_code"
            style={{ fontSize: "1.3rem", fontWeight: "bold" }}
          >
            reduce()
          </code>{" "}
          but processes elements from right to left.
        </p>
        <div className="code-demo-box">
          {" "}
          <CodeCopyBlock
            code={`const words = ["is", "this", "hello"];
const sentence = words.reduceRight((acc, word) => acc + " " + word);
console.log(sentence);`}
          />
          <TryItButton
            code={`const words = ["is", "this", "hello"];
const sentence = words.reduceRight((acc, word) => acc + " " + word);
console.log(sentence);`}
          />
        </div>
        <OutputBox>
          <pre>hello this is</pre>
        </OutputBox>
      </div>

      {/* 7️⃣ every() */}
      <div ref={refs.every} className="tutorial-section">
        <h2 className="section-title">7️⃣ Array every()</h2>
        <p>
          The{" "}
          <code
            className="highlight_code"
            style={{ fontSize: "1.3rem", fontWeight: "bold" }}
          >
            every()
          </code>{" "}
          method checks if all elements satisfy a condition.
        </p>
        <div className="code-demo-box">
          {" "}
          <CodeCopyBlock
            code={`const numbers = [2, 4, 6];
console.log(numbers.every(n => n % 2 === 0));`}
          />
          <TryItButton
            code={`const numbers = [2, 4, 6];
console.log(numbers.every(n => n % 2 === 0));`}
          />
        </div>
        <OutputBox>
          <pre>true</pre>
        </OutputBox>
      </div>

      {/* 8️⃣ some() */}
      <div ref={refs.some} className="tutorial-section">
        <h2 className="section-title">8️⃣ Array some()</h2>
        <p>
          The{" "}
          <code
            className="highlight_code"
            style={{ fontSize: "1.3rem", fontWeight: "bold" }}
          >
            some()
          </code>{" "}
          method checks if <b>any</b> element satisfies a condition.
        </p>
        <div className="code-demo-box">
          {" "}
          <CodeCopyBlock
            code={`const numbers = [1, 3, 5, 8];
console.log(numbers.some(n => n % 2 === 0));`}
          />
          <TryItButton
            code={`const numbers = [1, 3, 5, 8];
console.log(numbers.some(n => n % 2 === 0));`}
          />
        </div>
        <OutputBox>
          <pre>true</pre>
        </OutputBox>
      </div>

      {/* 9️⃣ Array from() */}
      <div ref={refs.from} className="tutorial-section">
        <h2 className="section-title">9️⃣ Array from()</h2>
        <p>
          The{" "}
          <code
            className="highlight_code"
            style={{ fontSize: "1.3rem", fontWeight: "bold" }}
          >
            from()
          </code>{" "}
          method creates an array from an iterable or string.
        </p>
        <div className="code-demo-box">
          {" "}
          <CodeCopyBlock
            code={`console.log(Array.from("Hello"));
console.log(Array.from([1, 2, 3], x => x * 2));`}
          />
          <TryItButton
            code={`console.log(Array.from("Hello"));
console.log(Array.from([1, 2, 3], x => x * 2));`}
          />
        </div>
        <OutputBox>
          <pre>["H","e","l","l","o"] [2,4,6]</pre>
        </OutputBox>
      </div>

      {/* 🔟 Array keys() */}
      <div ref={refs.keys} className="tutorial-section">
        <h2 className="section-title">🔟 Array keys()</h2>
        <p>
          The{" "}
          <code
            className="highlight_code"
            style={{ fontSize: "1.3rem", fontWeight: "bold" }}
          >
            keys()
          </code>{" "}
          method returns an iterator with array indexes.
        </p>
        <div className="code-demo-box">
          {" "}
          <CodeCopyBlock
            code={`const fruits = ["Banana", "Orange", "Apple"];
const keys = fruits.keys();
for (let key of keys) console.log(key);`}
          />
          <TryItButton
            code={`const fruits = ["Banana", "Orange", "Apple"];
const keys = fruits.keys();
for (let key of keys) console.log(key);`}
          />
        </div>
        <OutputBox>
          <pre>
            0{"\n"}1{"\n"}2
          </pre>
        </OutputBox>
      </div>

      {/* 11️⃣ Array entries() */}
      <div ref={refs.entries} className="tutorial-section">
        <h2 className="section-title">11️⃣ Array entries()</h2>
        <p>
          The{" "}
          <code
            className="highlight_code"
            style={{ fontSize: "1.3rem", fontWeight: "bold" }}
          >
            entries()
          </code>{" "}
          method returns key/value pairs.
        </p>
        <div className="code-demo-box">
          {" "}
          <CodeCopyBlock
            code={`const fruits = ["Banana", "Orange", "Apple"];
const entries = fruits.entries();
for (let [index, value] of entries) console.log(index, value);`}
          />
          <TryItButton
            code={`const fruits = ["Banana", "Orange", "Apple"];
const entries = fruits.entries();
for (let [index, value] of entries) console.log(index, value);`}
          />
        </div>
        <OutputBox>
          <pre>
            0 Banana{"\n"}1 Orange{"\n"}2 Apple
          </pre>
        </OutputBox>
      </div>

      {/* 12️⃣ Array with() */}
      <div ref={refs.withMethod} className="tutorial-section">
        <h2 className="section-title">12️⃣ Array with()</h2>
        <p>
          The{" "}
          <code
            className="highlight_code"
            style={{ fontSize: "1.3rem", fontWeight: "bold" }}
          >
            with()
          </code>{" "}
          method returns a new array with a changed element.
        </p>
        <div className="code-demo-box">
          {" "}
          <CodeCopyBlock
            code={`const numbers = [1, 2, 3, 4];
const newArr = numbers.with(1, 99);
console.log(newArr);`}
          />
          <TryItButton
            code={`const numbers = [1, 2, 3, 4];
const newArr = numbers.with(1, 99);
console.log(newArr);`}
          />
        </div>
        <OutputBox>
          <pre>[1, 99, 3, 4]</pre>
        </OutputBox>
      </div>

      {/* 13️⃣ Spread (...) */}
      <div ref={refs.spread} className="tutorial-section">
        <h2 className="section-title">13️⃣ Array Spread (...)</h2>
        <p>
          The{" "}
          <code
            className="highlight_code"
            style={{ fontSize: "1.3rem", fontWeight: "bold" }}
          >
            ...
          </code>{" "}
          spread operator expands elements into another array.
        </p>
        <div className="code-demo-box">
          {" "}
          <CodeCopyBlock
            code={`const arr1 = [1, 2];
const arr2 = [3, 4];
const combined = [...arr1, ...arr2];
console.log(combined);`}
          />
          <TryItButton
            code={`const arr1 = [1, 2];
const arr2 = [3, 4];
const combined = [...arr1, ...arr2];
console.log(combined);`}
          />
        </div>
        <OutputBox>
          <pre>[1, 2, 3, 4]</pre>
        </OutputBox>
      </div>

      {/* 14️⃣ Rest (...) */}
      <div ref={refs.rest} className="tutorial-section">
        <h2 className="section-title">14️⃣ Array Rest (...)</h2>
        <p>
          The{" "}
          <code
            className="highlight_code"
            style={{ fontSize: "1.3rem", fontWeight: "bold" }}
          >
            ...
          </code>{" "}
          rest syntax collects remaining elements into an array.
        </p>

        <div className="code-demo-box">
          {" "}
          <CodeCopyBlock
            code={`const [a, b, ...rest] = [10, 20, 30, 40, 50];
console.log(a, b);
console.log(rest);`}
          />
          <TryItButton
            code={`const [a, b, ...rest] = [10, 20, 30, 40, 50];
console.log(a, b);
console.log(rest);`}
          />
        </div>
        <OutputBox>
          <pre>10 20{"\n"}[30, 40, 50]</pre>
        </OutputBox>
      </div>
    </div>
  );
}
